// A thumbnail that is not viewable YET, and a tile that says so.
//
// ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
//
// 2026-08-24. Six photos in a customer's album showed a spinner that never
// stopped. Nothing was broken: they had arrived as HEIC, which no browser can
// draw, and the conversion sweep had not reached them yet. Within three minutes
// all six were fine — but for those three minutes the album looked broken, and
// the owner reported it as data loss on top of a real data-loss incident.
//
// ⚠️ AN ETERNAL SPINNER IS A LIE. It says "still working" for ever, whether the
// thing is two seconds from ready or will never arrive. The tile already fell
// back to the shimmer on an image error — which is kind, and honest for about
// five seconds — but it never retried, so it stayed there until the page was
// reloaded, and it never said why.
//
// Two jobs, both here so they can be tested without a browser:
//   1. Heal by itself. The URL works the moment the derivative exists; retrying
//      on a backoff turns a five-minute wait into a tile that fills in.
//   2. Stop pretending. After the first failure the tile says « en préparation »,
//      and once the retries are spent it says the preview is unavailable rather
//      than spinning for ever.
//
// ⚠️ IT IS NOT HEIC-SPECIFIC ON PURPOSE. A cold image transform, a signed URL
// that expired a second ago and a conversion that has not run yet are the same
// thing to a browser, and all three heal the same way.
//
// Guard: src/components/upload/tileRetry.test.ts

/**
 * How long to wait before asking for the image again.
 *
 * ⚠️ THE FIRST GAP IS SHORT AND THE LAST IS LONG, because the two causes have
 * very different clocks: an expired signed URL is fixed on the next list
 * refresh (seconds), while a HEIC waiting for the conversion sweep is fixed on
 * the next five-minute tick. The tail has to reach past that tick or the tile
 * gives up just before the thing it is waiting for arrives.
 */
export const RETRY_DELAYS_MS = [2_000, 6_000, 20_000, 60_000, 120_000, 200_000] as const;

/**
 * Delay before attempt number `attempt` (1-based), or null once we stop.
 *
 * ⚠️ IT DOES STOP. Retrying for ever costs the customer's battery and hides a
 * genuinely dead image behind an animation — the state after the last attempt
 * has to be a plain statement, not more waiting.
 */
export function retryDelayMs(attempt: number): number | null {
  if (!Number.isFinite(attempt) || attempt < 1) return RETRY_DELAYS_MS[0];
  return RETRY_DELAYS_MS[attempt - 1] ?? null;
}

/** Total time the tile will keep trying before it says so. */
export const RETRY_TOTAL_MS = RETRY_DELAYS_MS.reduce((a, b) => a + b, 0);

/**
 * The same URL, but one the browser will actually re-request.
 *
 * ⚠️ A FAILED IMAGE IS CACHED. Re-setting `src` to the identical string is a
 * no-op in every browser — the retry would look like it ran and change nothing.
 * A changing parameter is what makes it a new request.
 *
 * ⚠️ AND IT MUST NOT BREAK A SIGNED URL. Supabase and Cloudflare signatures
 * cover the path and their own parameters, not unknown extra ones, so an added
 * key is safe — but it has to be APPENDED correctly to a URL that already has a
 * query string, or the signature is what breaks.
 */
export function bustCache(url: string, attempt: number): string {
  const u = String(url ?? '');
  if (!u || attempt < 1) return u;
  const sep = u.includes('?') ? '&' : '?';
  return `${u}${sep}_r=${attempt}`;
}

/** What the tile is actually showing. */
export type TileState = 'image' | 'loading' | 'preparing' | 'unavailable' | 'empty';

/**
 * One place that decides, so the JSX cannot drift from the rule.
 *
 * ⚠️ « PREPARING » AND « LOADING » LOOK THE SAME AND MEAN DIFFERENT THINGS. The
 * first load is silent — a tile that announces itself before it has even failed
 * once is noise on a fast connection. Only once the image has actually failed
 * does the tile start explaining itself.
 */
export function tileState(args: {
  hasUrl: boolean;
  failed: boolean;
  attempt: number;
  loading?: boolean;
}): TileState {
  if (args.hasUrl && !args.failed) return 'image';
  if (args.hasUrl && args.failed) {
    return retryDelayMs(args.attempt + 1) === null ? 'unavailable' : 'preparing';
  }
  if (args.loading) return 'loading';
  return 'empty';
}
